import React, { useState, useRef, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import _ from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next/';

import './SportsBetting.scss';
import { RootState } from '../../configs/store';
import SportsBettingContext from '../../components/SportsBetting/Context/SportsBetting.context';
import Page from '../../containers/Page/Page';
import FilterButtons from '../../components/SportsBetting/FilterButtons/FilterButtons';
import SportTable from '../../components/SportsBetting/SportTable/SportTable';
import GoUpButton from '../../components/GoUpButton/GoUpButton';
import SportsBettingTitle from '../../components/SportsBetting/SportsBettingTitle/SportsBettingTitle';
import ScrollableSports from '../../components/SportsBetting/ScrollableSports/ScrollableSports';
import SearchResults from '../../components/SearchResults/SearchResults';
import usePreviousValue from 'src/hooks/usePreviousValue';
import useIsMobile from 'src/hooks/useIsMobile';
import FastMode from 'src/components/HorseBetting/FastMode/FastMode';
import AppModeContext from 'src/containers/AppMode/AppMode.context';

function SportsBetting() {
  // STATE
  const [maxMainMarkets, setMaxMainMarkets] = useState(1);
  const [maxFilterButtons, setMaxFilterButtons] = useState(2);
  const [selectedSport, setSelectedSport] = useState(null);
  const containerRef = useRef(null);
  const mobile = useIsMobile();

  const query = new URLSearchParams(location.search);
  const prevValue = usePreviousValue(query.get('card'));

  const context = {
    maxMainMarkets,
    maxFilterButtons,
    selectedSport,
    setSelectedSport
  };

  const { RETAIL_MODE } = useContext(AppModeContext);

  // REDUX
  const { events, isSearching, sport, filterSports: { top, type }, liveOutcomes, selectedEvent } = useSelector((state: RootState) => state.events);
  const { configs: { sports } } = useSelector((state: RootState) => state.content);
  const customerId = useSelector((state: RootState) => state.auth.customerId);
  const { t } = useTranslation('');

  const dispatch = useDispatch();

  const componentResized = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.getBoundingClientRect().width;

      if (containerWidth > 0 && containerWidth <= 600 && maxMainMarkets > 1) {
        setMaxMainMarkets(1);
      } else {
        const max = Math.floor((containerWidth - (!isMobile ? 500 : 300)) / 144);
        setMaxMainMarkets(max > 0 ? max : 1);
      }

      const maxFilterWidth = Math.floor(containerWidth / 220);
      if (maxFilterWidth !== maxFilterButtons && maxFilterWidth > 0) {
        setMaxFilterButtons(maxFilterWidth);
      }
    }
  };

  useEffect(() => {
    if ((mobile && !selectedSport) || !mobile){
      if (top){
        dispatch.events.getEventsByTop(top);
      } else {
        dispatch.events.getSportEvents();
      }
    } else {
      dispatch.events.getSportEventsListBySport(selectedSport);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', componentResized);
    componentResized();

    dispatch.events.clearSelectedTop(true);

    return () => {
      window.removeEventListener('resize', componentResized);
    };
  }, []);

  useEffect(() => {
    const resizeObs = new ResizeObserver(componentResized);

    if (containerRef.current) {
      resizeObs.observe(containerRef.current);
    }

    return () => {
      resizeObs.disconnect();
    };
  }, [containerRef.current]);

  useEffect(() => {
    if (query.has('card') && prevValue !== query.get('card')) {
      const card = query.get('card');
      if (card === '9') {
        dispatch.peripherals.getLastMatrix();
      } else {
        dispatch.peripherals.getCardbyId(card);
      }
      dispatch.peripherals.changeBetInfo(true);
    }
  }, [query]);

  useEffect(() => {
    if (!_.isNull(sport)) {
      setSelectedSport(sport);
    }
  }, [sport]);

  useEffect(() => {
    let response = null;
    if (_.isEmpty(selectedEvent)){
      const tillId = localStorage.getItem('tillId') ? localStorage.getItem('tillId') : -1;

      //THIS CALL NEEDS TO BE HERE SO THAT WE CAN CLOSE THE EVENTSOURCE, this will be done different when we have the live events.
      response = new EventSource(`${ process.env.REACT_APP_BASE_URL
      }/bets/check-outcomes/${ liveOutcomes.join(',') }?tillId=${ tillId }&customerId=${ customerId ? customerId : -1 }`);
      response.onmessage = (e) => dispatch.events.updateEvent(e.data);
    }

    return () => {
      if (response){
        response.close();
      }
    };
  }, [liveOutcomes]);

  return (
    <Page>
      <Helmet>
        <title>BetXchange - Sports</title>
        <meta name="description" content={ sports?.description } />
        <meta name="keywords" content={ sports?.keywords } />
      </Helmet>
      {
        RETAIL_MODE && <FastMode >
          <FastMode.Sports />
        </FastMode>
      }
      {
        isSearching && !mobile
          ? <SearchResults>
            <SearchResults.Sports />
          </SearchResults>
          : (
            <SportsBettingContext.Provider value={ context }>
              <div className="sports-betting" ref={ containerRef }>
                { mobile && <ScrollableSports /> }

                { type === '1,8' && <FilterButtons /> }

                {
                  <>
                    <SportsBettingTitle />

                    {
                      events && _.map(events, (sport) => {
                        return (
                          <SportTable key={ `upcoming_${ sport.code }` } sport={ sport } />
                        );
                      })
                    }
                    { !events && <div>{ t('sports.noResultsLive') } </div> }
                  </>
                }

                { !selectedEvent && <GoUpButton /> }
              </div>
              { type === '2,8' && <FilterButtons /> }
            </SportsBettingContext.Provider>
          )
      }
    </Page>
  );
}

export default React.memo(SportsBetting);
